import type { ComponentProps } from 'react';
import { Check } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';

interface SaveButtonProps extends ComponentProps<typeof Button> {
  /** The save is in flight. */
  pending: boolean;
  /** The last save just succeeded (`useSavedFlash`). */
  saved: boolean;
}

/**
 * A form's submit button that confirms in place (FE-ADR-065): its label reads
 * "Saving…" while the save runs, then "Saved ✓" for a moment, so a form that
 * stays on screen needs no success toast. While it reads "Saved" it can't be
 * pressed (nothing new to save) but isn't dimmed; `disabled` is the caller's
 * own rule (e.g. a clean form). Errors are still reported where they were
 * (field errors, or a toast).
 */
export function SaveButton({
  pending,
  saved,
  children,
  disabled,
  className,
  ...props
}: SaveButtonProps) {
  return (
    <>
      <Button
        disabled={disabled || pending || saved}
        className={cn(saved && 'disabled:opacity-100', className)}
        {...props}
      >
        {pending ? (
          'Saving…'
        ) : saved ? (
          <>
            <Check aria-hidden /> Saved
          </>
        ) : (
          children
        )}
      </Button>
      <span role="status" aria-live="polite" className="sr-only">
        {saved ? 'Saved' : ''}
      </span>
    </>
  );
}
